import React from "react";
import { Search, CheckCircle2, Users, ListFilter } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { PageShell } from "../components/PageShell";
import { Card, Avatar, Badge } from "../components/primitives";
import { DataTable, EmptyState, Pagination } from "../components/DataTable";
import { StatStrip } from "../components/StatStrip";
import { ToolSelect } from "../components/ToolSelect";
import { APP_CATALOG } from "../../../modules/apps/appCatalog";
import { AppIcon } from "../../../modules/apps/AppIcon";
import { getMemberToken } from "../../../modules/email-insights/engine/backendClient";
import { useTeamInsights } from "../../../modules/email-insights/hooks/useTeamInsights";
import { useNavigate } from "react-router-dom";
import { DASHBOARD_BASE } from "../../../config/featureFlags";

const CONNECTION_FILTERS = [
  { value: null, label: "All members" },
  { value: "connected", label: "Gmail connected" },
  { value: "not_connected", label: "Gmail not connected" },
];

// One badge per catalog app on a member row. Only Gmail carries real data
// today — every other app is a "coming soon" placeholder until its own
// connector module ships, so this page never lies about connections it
// can't actually see.
function AppBadge({ app, member, onViewInside, viewLoading }) {
  const t = useDashTheme();

  if (app.id !== "gmail") {
    return (
      <span title={`${app.name} — coming soon`} style={{ display: "inline-flex" }}>
        <Badge tone="neutral"><AppIcon app={app} size={11} style={{ opacity: 0.55 }} /> {app.name}</Badge>
      </span>
    );
  }

  const connected = !!member.gmailConnected;
  return (
    <button
      onClick={connected ? () => onViewInside(member, app) : undefined}
      disabled={!connected || viewLoading}
      title={connected ? `View ${app.name} insights` : `${app.name} not connected`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px 2px 6px",
        borderRadius: 99, border: "none", cursor: connected ? "pointer" : "default",
        background: connected ? t.status.positiveSoft : t.status.neutralSoft,
        color: connected ? t.status.positive : t.status.neutral,
        fontSize: t.font.size.xs, fontWeight: t.font.weight.medium, fontFamily: t.font.family,
        opacity: viewLoading ? 0.6 : 1,
      }}
    >
      <AppIcon app={app} size={12} />
      {app.name}
      {connected && <CheckCircle2 size={11} />}
    </button>
  );
}

// "Team Apps" — every user's own hierarchy (direct + indirect reportees),
// never peers or the whole org. Reuses the exact reportee BFS + pagination
// that used to power the "Team Insights" tab inside Email Insights; that tab
// is gone now that this is its own page, so this is the one place team app
// usage shows up. Built on the same PageShell + StatStrip + DataTable shell
// every other list screen (Team management, HRMS People, ...) uses.
export default function TeamAppsV2() {
  const t = useDashTheme();
  const team = useTeamInsights();
  const [q, setQ] = React.useState("");
  const [filter, setFilter] = React.useState(null); // null | "connected" | "not_connected"

  const [viewLoadingId, setViewLoadingId] = React.useState(null);
  const [viewError, setViewError] = React.useState(null);
  const navigate = useNavigate();

  const connectedCount = team.team.filter((m) => m.gmailConnected).length;

  const members = React.useMemo(() => {
    let list = team.team;
    if (filter === "connected") list = list.filter((m) => m.gmailConnected);
    if (filter === "not_connected") list = list.filter((m) => !m.gmailConnected);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((m) => (m.name || "").toLowerCase().includes(s) || (m.email || "").toLowerCase().includes(s));
    }
    return list;
  }, [team.team, filter, q]);

  // Per-member drill-down ab Email Insights page pe rehta hai (TeamInsightsTab ke
  // andar member view). Yahan token check karke wahin bhej dete hain, taaki
  // "Gmail connected nahi hai" wala error pehle jaisa hi dikhe.
  const handleViewInside = React.useCallback(async (member, app) => {
    setViewError(null);
    setViewLoadingId(member.userId);
    try {
      await getMemberToken(member.userId);
      navigate(`${DASHBOARD_BASE}/email-insights`);
    } catch (err) {
      setViewError(err.message || `Failed to open ${app.name} insights.`);
    } finally {
      setViewLoadingId(null);
    }
  }, [navigate]);

  const filtersActive = !!q || !!filter;

  return (
    <PageShell
      eyebrow="Workspace · Apps"
      title="Team Apps"
      subtitle="Everyone in your reporting line — direct and indirect — and the apps they use."
      icon={<Users size={22} />}
      topRight={
        <div className="tg-filterbar">
          <label className="tg-tool tg-tool-search">
            <Search size={14} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your team" aria-label="Search your team" />
          </label>
          <span className="tg-tool-sep" />
          <ToolSelect
            icon={<ListFilter size={14} />}
            value={filter}
            options={CONNECTION_FILTERS}
            onChange={setFilter}
            placement="bottomRight"
            panelWidth={200}
          />
        </div>
      }
    >
      <StatStrip items={[
        { label: "in your reporting line", value: team.total, accent: t.accents[0] },
        { label: "Gmail connected", value: connectedCount, accent: t.accents[1] },
        { label: "other apps live", value: 0, accent: t.accents[7] },
      ]} />

      {viewError && (
        <div style={{ marginBottom: t.s[3], padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.md, background: t.status.negativeSoft, color: t.status.negative, fontSize: t.font.size.sm }}>
          {viewError}
        </div>
      )}

      <Card padding={0}>
        <DataTable
          columns={[
            {
              key: "member", header: "Member",
              render: (m) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, maxWidth: 280 }}>
                  <Avatar user={m} name={m.name} size={30} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name || m.email}</div>
                    <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.email}{m.role ? ` · ${m.role}` : ""}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: "apps", header: "Apps",
              render: (m) => (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {APP_CATALOG.map((app) => (
                    <AppBadge key={app.id} app={app} member={m} onViewInside={handleViewInside} viewLoading={viewLoadingId === m.userId} />
                  ))}
                </div>
              ),
            },
          ]}
          rows={members}
          rowKey={(m) => m.userId}
          loading={team.loading}
          skeletonRows={6}
          emptyState={
            <EmptyState icon={<Users size={22} />} title="No reportees found"
              description={filtersActive ? "No one matches these filters." : "Nobody reports to you yet — this page fills in once your org chart does."} />
          }
        />
        <Pagination
          page={team.page} pages={team.totalPages} total={team.total} pageLabel="people"
          onChange={(p) => team.goToPage(p)}
          pageSize={team.pageSize} onPageSizeChange={(size) => team.goToPage(1, size)}
          pageSizeOptions={[12, 24, 48]}
          shown={members.length}
        />
      </Card>
    </PageShell>
  );
}
